import { KeyRound, Settings } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import type { IntegrationCardData, Integration } from "../types";
import { getStatusBadgeType, getStatusLabelKey } from "../utils";
import { GenerateApiKeyDialog } from "./GenerateApiKeyDialog";
import { IntegrationConfigurationDialog } from "./IntegrationConfigurationDialog";
import IntegrationModuleChips from "./IntegrationModuleChips";
import { VACATURES_ONLINE_INTEGRATIONS, VacaturesOnlineConfigurationDialog } from "./vacaturesonline/VacaturesOnlineConfigurationDialog";
import { encodeSlug } from "../utils/slug";

import { Avatar, AvatarFallback } from "@/components/ui/v2/avatar";
import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/v2/card";
import { useAuth } from "@/lib/hooks/useAuth";
import type { ConfigurationContext } from "@/types/configuration";
import { UserRole } from "@/types/user/userType";

// Props interface following V2 patterns - no React.FC
interface IntegrationCardProps {
  integration: IntegrationCardData;
  onManage: (integrationName: string) => void;
  integrationDetails?: Integration; // Complete integration object with configuration schema
}

// Component using function declaration (not React.FC)
function IntegrationCard({
  integration,
  onManage,
  integrationDetails,
}: IntegrationCardProps) {
  const { t, i18n } = useTranslation();
  const { tenantId, role } = useAuth();
  const { clientId } = useParams<{ clientId?: string }>();
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);

  // API keys are integration-level, managed at the tenant level by SuperAdmins, and only make sense when
  // the integration actually grants scopes (i.e. has an active module with at least one scope).
  const hasScopedModule = integration.modules.some(
    (m) => m.isActive && m.scopes.length > 0
  );
  const canManageApiKeys = role === UserRole.SUPER_ADMIN && !clientId && hasScopedModule;

  const handleManageApiKeys = (e: React.MouseEvent) => {
    e.stopPropagation();
    setApiKeyDialogOpen(true);
  };
  const statusBadgeType = getStatusBadgeType(integration.isActive);
  const statusLabel = t(getStatusLabelKey(integration.isActive));

  const navigateToDetail = () => {
    if (integration.name) {
      onManage(encodeSlug(integration.name));
    }
  };

  const handleConfigure = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfigDialogOpen(true);
  };

  // Translate description if it's a translation key, otherwise use as-is
  const description = integration.description && i18n.exists(integration.description)
    ? t(integration.description) : integration.description || t("integrations.descriptions.default");

  // Translate display name if it's a translation key, otherwise use as-is
  const displayName = integration.displayName && i18n.exists(integration.displayName)
    ? t(integration.displayName)
    : integration.displayName;

  // Build context for configuration dialog
  const context: ConfigurationContext = clientId
    ? {
      level: 'client',
      tenantId: tenantId || null,
      clientId: clientId,
    }
    : {
      level: 'tenant',
      tenantId: tenantId || null,
    };

  return (
    <>
      <Card
        hover
        className="cursor-pointer flex flex-col justify-between h-full"
        onClick={navigateToDetail}
      >
        <CardHeader className="flex flex-row items-start gap-4">
          <div className="flex items-center justify-center">
            <Avatar>
              <AvatarFallback>
                {displayName
                  ?.split(/\s+/)
                  .map((word) => word[0])
                  .join("")
                  .toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </div>
          <div className="flex-1">
            <CardTitle className="text-base font-semibold leading-6">
              {displayName}
            </CardTitle>
            <CardDescription className="line-clamp-2">{description}</CardDescription>
          </div>
        </CardHeader>

        {integration.modules.length > 0 && (
          <CardContent>
            <IntegrationModuleChips modules={integration.modules} />
          </CardContent>
        )}

        <CardFooter className="flex flex-row-reverse items-center justify-between border-t border-border">
          <div>
            <Badge variant={statusBadgeType} size="medium">
              {statusLabel}
            </Badge>
          </div>
          <div className="flex gap-2">
            {canManageApiKeys && (
              <Button variant="outline" size="sm" onClick={handleManageApiKeys}>
                <KeyRound size={16} />
                {t("integrations.apiKey.manage", "API Keys")}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleConfigure}>
              <Settings size={16} />
              {t("common.configure", "Configure")}
            </Button>
          </div>
        </CardFooter>
      </Card>

      {/* Configuration Dialog — VacaturesOnline integrations get a custom dialog at client level */}
      {configDialogOpen && (() => {
        const integrationProp = integrationDetails || {
          id: integration.id,
          name: integration.name,
          displayName: integration.displayName,
          description: integration.description,
          isActive: integration.isActive,
          configurationSchemaJson: integration.configurationSchemaJson,
          modules: integration.modules,
        };

        const isVacaturesOnlineClient =
          clientId && VACATURES_ONLINE_INTEGRATIONS.has(integration.name);

        if (isVacaturesOnlineClient) {
          return (
            <VacaturesOnlineConfigurationDialog
              open={configDialogOpen}
              onOpenChange={setConfigDialogOpen}
              integration={integrationProp}
              context={context}
            />
          );
        }

        return (
          <IntegrationConfigurationDialog
            open={configDialogOpen}
            onOpenChange={setConfigDialogOpen}
            integration={integrationProp}
            context={context}
          />
        );
      })()}

      {/* Integration-level API Key management (tenant / SuperAdmin only) */}
      {canManageApiKeys && apiKeyDialogOpen && (
        <GenerateApiKeyDialog
          open={apiKeyDialogOpen}
          onOpenChange={setApiKeyDialogOpen}
          integrationName={integration.name}
          integrationDisplayName={displayName}
          modules={integration.modules}
        />
      )}
    </>
  );
}

export default IntegrationCard;
